/**
 * Modal.tsx — a minimal, accessible modal shell shared by NodeModal/EdgeModal.
 *
 * No dependency — just a fixed overlay + a panel + Escape-to-close. The
 * monorepo's demos all roll their own modal the same way; we keep that
 * pattern but in React. `headerLeft` lets a caller inject a leading element
 * (the NodeModal uses it for the icon+kind-select strip the spec requires).
 */
import { useEffect, useState, type ReactNode } from 'react';

interface ModalProps {
  /** Title text. When omitted, the header renders only the headerLeft slot +
   *  the close button (used by NodeModal where the icon + kind select IS the
   *  header — no redundant text label). */
  title?: string;
  /** Optional element rendered before the title (e.g. the node-icon button). */
  headerLeft?: ReactNode;
  /** Optional element rendered after the title, before the close button
   *  (e.g. the AgentModal's API/Code tab strip). */
  headerRight?: ReactNode;
  /** Optional sticky footer rendered below the body (e.g. the AgentModal's
   *  action buttons). */
  footer?: ReactNode;
  /** Width preset. `medium` (default) = 440px; `wide` = 600px; `xwide` = 960px
   *  (the ChartEditor's two-pane form + live preview); `fullscreen` = a
   *  bottom-sheet (full width, ~90vh, pinned to viewport bottom, no bottom
   *  radius — the ChatModal owns its own two-pane layout, uses flushBody). */
  size?: 'medium' | 'wide' | 'xwide' | 'fullscreen';
  /** When true, the body has no padding (the content manages its own). Used by
   *  the SettingsModal so the tab strip's border-right meets the panel edge
   *  cleanly without double padding. */
  flushBody?: boolean;
  /** When true, the modal renders NO header bar at all (no title, no close
   *  button) — the caller owns its own chrome + close affordance. Used by the
   *  ChatModal, whose sidebar holds its own close button; the title bar would
   *  just waste vertical space. */
  hideHeader?: boolean;
  /** When provided, the Modal manages its own animated mount/unmount: the
   *  backdrop fades and (for the `fullscreen` bottom-sheet size) the panel
   *  slides up from the bottom. The caller should ALWAYS render
   *  `<Modal isOpen={…}>` (not conditionally mount it) so the exit animation
   *  can play before unmount. When omitted, the Modal mounts immediately and
   *  the caller controls unmounting (legacy behaviour, no animation). */
  isOpen?: boolean;
  /** When false, the Modal cannot be dismissed by the user: no Escape key, no
   *  overlay click, no ✕ button — only the caller's own controls (the footer)
   *  can close it. Used by the LicenseGate, which must not be dismissable
   *  before the license is accepted. Default true. */
  dismissable?: boolean;
  /** Extra class on the overlay (e.g. 'modal-overlay--opaque' for the
   *  LicenseGate's non-transparent backdrop). */
  overlayClassName?: string;
  onClose: () => void;
  children: ReactNode;
}

/** Must match the longest CSS transition on the animated overlay/panel
 *  (the bottom-sheet slide). Modal waits this long before unmounting on close
 *  so the exit animation can play. See `.modal-overlay--animated` in style.css. */
const MODAL_ANIM_MS = 320;

export function Modal({ title, headerLeft, headerRight, footer, size = 'medium', flushBody = false, hideHeader = false, isOpen, dismissable = true, overlayClassName, onClose, children }: ModalProps): JSX.Element | null {
  // Animated mount/unmount is opt-in via `isOpen`. When omitted the Modal is
  // always rendered (caller-controlled mount) — the legacy immediate behaviour
  // every other modal still uses.
  const animated = isOpen !== undefined;
  const [render, setRender] = useState(animated ? !!isOpen : true);
  const [visible, setVisible] = useState(false);

  // Drive mount/unmount from isOpen (animated path only). On close we keep the
  // overlay mounted for MODAL_ANIM_MS so the exit transition finishes.
  useEffect(() => {
    if (!animated) return undefined;
    if (isOpen) {
      setRender(true);
      return undefined;
    }
    setVisible(false);
    const t = setTimeout(() => setRender(false), MODAL_ANIM_MS);
    return () => clearTimeout(t);
  }, [isOpen, animated]);

  // After the in-state renders + paints, flip .is-visible on to trigger the
  // enter transition. Double rAF guarantees the start frame (opacity:0 /
  // translateY(100%)) is committed before we transition away from it — a single
  // rAF can be batched with the mount paint and skip the animation entirely.
  useEffect(() => {
    if (!animated || !render || !isOpen) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setVisible(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [animated, render, isOpen]);

  useEffect(() => {
    if (!dismissable) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, dismissable]);

  if (!render) return null;

  const overlayCls = `modal-overlay${animated ? ' modal-overlay--animated' : ''}${visible ? ' is-visible' : ''}${overlayClassName ? ` ${overlayClassName}` : ''}`;

  return (
    <div className={overlayCls} onClick={dismissable ? onClose : undefined}>
      <div
        className={`modal-panel${size === 'wide' ? ' wide' : ''}${size === 'xwide' ? ' xwide' : ''}${size === 'fullscreen' ? ' fullscreen' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        {!hideHeader && (
        <header className="modal-header">
          {headerLeft && <div className="modal-header-left">{headerLeft}</div>}
          {title && <h2>{title}</h2>}
          {headerRight && <div className="modal-header-right">{headerRight}</div>}
          {dismissable && (
            <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          )}
        </header>
        )}
        <div className={`modal-body${flushBody ? ' flush' : ''}`}>{children}</div>
        {footer && <footer className="modal-footer">{footer}</footer>}
      </div>
    </div>
  );
}
